<script lang="ts">
  // packages
  import { intcomma, pluralize } from 'journalize';

  // utils
  import { formatPercent } from '../utils/format.js';
  import {
    isMeasure,
    isRunoff,
    isWinner,
    measureLabel,
    orderedChoices,
    partyClass,
    yesNo,
  } from '../utils/race-helpers.js';

  // types
  import type { Readable } from 'svelte/store';
  import type { Race } from '../types';

  export let feed: Readable<Race | undefined>;

  let alwaysShowCandidates = 4;
  let voteLabel = 'total';
  let hidePrecincts = false;
  let viewAllCandidates = false;

  $: race = $feed;
  // Yes then No before votes, then by votes (same order as the other tables)
  $: choices = race ? orderedChoices(race) : [];
  $: totalVotes = race?.attributes.totalVotes ?? 0;
  $: eevp = race?.attributes.eevp;
  $: tooFewToHide = choices.length <= alwaysShowCandidates + 1;
  $: isBallotMeasure = race ? isMeasure(race) : false;
</script>

<!-- race is undefined if its ID isn't in the feed -->
{#if race}
  <div class="contest mt-0">
    <table class="contest__candidates">

      <!-- HEADER -->
      <thead>
        <tr>
          <th scope="col" colSpan={1} class="contest__candidate-header" />
          <th scope="col" colSpan="1" class="contest__candidate-header">
          </th>
          <th scope="col" class="contest__candidate-header text-left">
            <span>% votes</span>
          </th>
          <th scope="col" class="contest__candidate-header text-left">
            {voteLabel}
          </th>
        </tr>
      </thead>

      <tbody>
        {#each choices as candidate, idx (candidate.choiceId)}
          <tr
            class="candidate contest__candidate-row contest__candidate-row-{partyClass(candidate)}"
            hidden={!(
              viewAllCandidates ||
              tooFewToHide ||
              idx < alwaysShowCandidates
            )}
          >
            <th
              scope="row"
              class="candidate__cell candidate__cell-name"
              class:candidate__cell-name-winner={isWinner(candidate) ||
                isRunoff(candidate)}
            >
              {#if isWinner(candidate)}
                <span class="candidate__cell-winner candidate__{partyClass(candidate)}" aria-label="checkmark, winner">
                  ✓
                </span>
              {/if}
            </th>
            <th
              scope="row"
              class="candidate__cell candidate__cell-name"
              class:candidate__cell-name-winner={isWinner(candidate) ||
                isRunoff(candidate)}
            >
              {measureLabel(candidate)}
            </th>

            <td
              class="candidate__cell candidate__cell-pct candidate__cell-pct-{isWinner(candidate)
                ? 'w'
                : 'l'} text-left"
              class:candidate__cell-ballot-measures={isBallotMeasure}
            >
              <div>
                <span
                  class="candidate__pct-bar candidate__pct-bar-{yesNo(candidate)} "
                  style="width: {formatPercent(candidate.votes, totalVotes)}%"
                />
                  {formatPercent(candidate.votes, totalVotes)}<span class="candidate__pct-symbol">%</span>
              </div>
            </td>

            <td
              class="candidate__cell candidate__cell-votes text-right"
              class:candidate__cell-ballot-measures={isBallotMeasure}
            >
              {intcomma(candidate.votes)}
            </td>
          </tr>
        {/each}
        {#if !tooFewToHide}
          <tr class="candidate contest__candidate-row">
            <th colspan="4" scope="row" class="candidate__cell p-0" >
              <button on:click={() => (viewAllCandidates = !viewAllCandidates)}>
                View
                {viewAllCandidates ? 'fewer' : 'all'}
                candidates
                <svg
                  class="icon"
                  class:icon-angle-down={!viewAllCandidates}
                  class:icon-angle-up={viewAllCandidates}
                >
                  <use
                    xlink:href="#{viewAllCandidates
                      ? 'icon-angle-up'
                      : 'icon-angle-down'}"
                  />
                </svg>
              </button>
            </th>
          </tr>
        {/if}
      </tbody>
    </table>
    <!-- expected vote on the left, total votes on the right, like 2024 -->
    <div class="table-footer-row">
      <div class="contest__precincts">
        <span class="text-left">
          {#if !hidePrecincts && eevp != null && eevp >= 0 && (eevp > 0 || totalVotes === 0)}
            {eevp.toFixed(1)}% of expected vote in
          {/if}
        </span>
        <span class="text-right">
          {intcomma(totalVotes)} total vote{pluralize(totalVotes)}
        </span>
      </div>
    </div>
  </div>
{/if}
